import { useRef } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/features/auth/use-auth';
import { useAuthDialog } from '@/features/auth/use-auth-dialog';
import { useMilestones, useMyMilestones } from '@/features/milestones/api';
import { MilestoneWorld } from '@/features/milestones/components/MilestoneWorld';
import type { IslandVisual } from '@/features/milestones/scene/types';
import { useMarketProducts } from '../../api';
import { coins } from '../../format';
import './home-rewards.css';

const number = (value: number) =>
  new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 }).format(value);

export function HomeRewardsSection() {
  return (
    <section className="home-rewards" aria-labelledby="home-rewards-title">
      <header className="home-rewards-heading">
        <div>
          <p className="home-rewards-kicker">GEEFAMILY’DE HER ADIMIN BİR KARŞILIĞI VAR</p>
          <h2 id="home-rewards-title">
            Kazan. Keşfet. <em>Kendin seç.</em>
          </h2>
        </div>
        <Link to="/geecoin" className="section-link">
          GeeCoin’i tanı <span aria-hidden>↗</span>
        </Link>
      </header>
      <div className="home-rewards-grid">
        <MarketPreview />
        <MilestonePreview />
      </div>
    </section>
  );
}

function MarketPreview() {
  const products = useMarketProducts();
  const items = products.data?.slice(0, 3) ?? [];
  return (
    <section className="home-market" aria-labelledby="home-market-title">
      <header className="home-preview-heading">
        <div>
          <p className="home-rewards-kicker">BİRİKTİRDİKLERİN SENİN</p>
          <h3 id="home-market-title">GeeFamily Market</h3>
        </div>
        <span className="home-gc-badge">
          <span aria-hidden>G</span> GeeCoin ile
        </span>
      </header>
      <p className="home-preview-description">
        Topluluktan kazandığın puanları, sana özel bir seçime dönüştür.
      </p>
      {products.isPending ? (
        <div className="home-market-loading" role="status">
          Koleksiyon yükleniyor…
        </div>
      ) : products.isError ? (
        <div className="home-preview-message" role="alert">
          <strong>Market şu an yüklenemedi.</strong>
          <button
            className="home-preview-link"
            onClick={() => {
              void products.refetch();
            }}
          >
            Tekrar dene ↗
          </button>
        </div>
      ) : items.length ? (
        <div className="home-market-products">
          {items.map((product) => (
            <Link
              to="/market#market-catalog"
              className="home-market-product"
              key={product.id}
              aria-label={`${product.title} · ${coins(product.coinPrice)} GeeCoin${product.stock === 0 ? ' · Tükendi' : ''}`}
            >
              <div className="home-market-image">
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt="" loading="lazy" width={320} height={280} />
                ) : (
                  <span className="home-product-mark" aria-hidden>
                    G
                  </span>
                )}
                {product.stock === 0 ? <span className="home-product-stock">Tükendi</span> : null}
              </div>
              <div className="home-product-copy">
                <span className="home-product-category">{product.category}</span>
                <h4>{product.title}</h4>
                <p>
                  <span aria-hidden className="home-mini-coin">
                    G
                  </span>{' '}
                  {coins(product.coinPrice)} <small>GC</small>
                </p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="home-market-empty">
          <div className="home-collection-art" aria-hidden>
            <span>G</span>
            <i />
            <b>
              GEE
              <br />
              FAMILY
            </b>
          </div>
          <div>
            <strong>İlk koleksiyon hazırlanıyor.</strong>
            <p>
              Ürünler yayınlandığında burada keşfedebilirsin. Bu sırada GeeCoin yolculuğuna göz at.
            </p>
          </div>
        </div>
      )}
      <footer className="home-preview-footer">
        <span>Etkinliklerle kazan · Market’te kullan</span>
        <Link to="/market" className="home-preview-link">
          Market’i keşfet <span aria-hidden>↗</span>
        </Link>
      </footer>
    </section>
  );
}

function MilestonePreview() {
  const { isAuthenticated, isLoading } = useAuth();
  const { open } = useAuthDialog();
  const definitions = useMilestones();
  const progress = useMyMilestones();
  const roadmap = useRef<HTMLDivElement>(null);
  const rows = isAuthenticated ? progress.data?.rows : undefined;
  // Keep the server's roadmap order and currency/site scopes. Ready chests take priority.
  const row =
    rows?.find((item) => item.status === 'ready') ??
    rows?.find((item) => item.status !== 'claimed') ??
    rows?.at(-1);
  const milestone = isAuthenticated ? row?.milestone : definitions.data?.[0];
  const loading = isLoading || (isAuthenticated ? progress.isPending : definitions.isPending);
  const failed = isAuthenticated ? progress.isError : definitions.isError;
  const ready = row?.status === 'ready';
  const complete = Boolean(rows?.length) && rows?.every((item) => item.status === 'claimed');
  const unlinked = isAuthenticated && progress.data?.linkedAccountCount === 0;
  const percent = row ? Math.max(0, Math.min(100, row.progressPercent)) : 0;
  const visual: IslandVisual = {
    id: milestone?.id ?? 'home-island-preview',
    theme: milestone?.theme ?? 'emerald',
    state:
      !failed && !loading && row?.status === 'claimed'
        ? 'claimed'
        : !failed && !loading && ready
          ? 'ready'
          : 'locked',
  };
  return (
    <section className="home-milestone" aria-labelledby="home-milestone-title">
      <header className="home-preview-heading">
        <div>
          <p className="home-rewards-kicker">MILESTONE YOLCULUĞUN</p>
          <h3 id="home-milestone-title">
            {!loading && !failed && ready
              ? 'Sandığın seni bekliyor.'
              : !loading && !failed && complete
                ? 'Adaların tamamlandı.'
                : 'Sıradaki ada senin.'}
          </h3>
        </div>
        <Link
          to="/milestone-rewards"
          className="home-island-arrow"
          aria-label="Milestone yolculuğunu keşfet"
        >
          ↗
        </Link>
      </header>
      <div className="home-island" ref={roadmap}>
        <div className="home-island-orbit" aria-hidden />
        <div className="home-island-anchor" data-island-id={visual.id}>
          <div className="home-island-fallback" aria-hidden>
            <span />
            <i />
            <b />
          </div>
        </div>
        <MilestoneWorld roadmap={roadmap} items={[visual]} />
        <span className="home-island-caption">
          {!loading && !failed && milestone
            ? (milestone.siteName ?? 'Tüm partnerler')
            : 'GEEFAMILY ISLANDS'}
        </span>
      </div>
      <div className="home-milestone-info">
        {loading ? (
          <p role="status">Yolculuğun yükleniyor…</p>
        ) : failed ? (
          <div role="alert">
            <strong>Yolculuğun yüklenemedi.</strong>
            <button
              className="home-preview-link"
              onClick={() => {
                void (isAuthenticated ? progress.refetch() : definitions.refetch());
              }}
            >
              Tekrar dene ↗
            </button>
          </div>
        ) : !milestone ? (
          <>
            <strong>Yeni adalar hazırlanıyor.</strong>
            <p>Milestone hedefleri yayınlandığında yolculuğun burada görünecek.</p>
          </>
        ) : (
          <>
            <div className="home-milestone-target">
              <h4>{milestone.title}</h4>
              <span>
                {row?.status === 'claimed' ? 'Alındı · ' : '+'}
                {coins(row?.claim?.rewardCoins ?? milestone.rewardCoins)} GC
              </span>
            </div>
            {isAuthenticated && row ? (
              <>
                <div className="home-progress-copy">
                  <span>
                    {number(row.wagered)} / {number(milestone.threshold)} {milestone.currency}
                  </span>
                  <strong>%{number(percent)}</strong>
                </div>
                <div
                  className="home-progress-track"
                  role="progressbar"
                  aria-label={`${milestone.title} ilerlemesi`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                >
                  <span style={{ width: `${percent}%` }} />
                </div>
                <p>
                  {unlinked
                    ? 'Oyun hesabını bağla; eşleşen geçmiş yarışmaların da sayılsın.'
                    : complete
                      ? 'Yayınlanan tüm sandıkların ödüllerini topladın.'
                      : ready
                        ? 'Hedef tamamlandı. Adana git ve ödülünü al.'
                        : `${row.eligibleRaceCount} tamamlanmış yarışmadan hesaplandı.`}
                </p>
              </>
            ) : (
              <p>
                {number(milestone.threshold)} {milestone.currency} çevrim hedefi. Kendi ilerlemeni
                görmek için giriş yap.
              </p>
            )}
          </>
        )}
      </div>
      <footer className="home-preview-footer">
        {!loading && !failed && isAuthenticated && progress.data ? (
          <span>
            {progress.data.claimedCount} sandık açıldı
            {progress.data.claimableCount > 0 ? ` · ${progress.data.claimableCount} hazır` : ''}
          </span>
        ) : (
          <span>Hedefe ulaş · Sandığını aç</span>
        )}
        {!isLoading && !isAuthenticated ? (
          <button className="home-preview-link" onClick={() => open('login')}>
            Giriş yap ↗
          </button>
        ) : (
          <Link
            className="home-preview-link"
            to={!failed && unlinked ? '/profile' : '/milestone-rewards'}
          >
            {!failed && unlinked
              ? 'Hesabını bağla'
              : !failed && ready
                ? 'Sandığına git'
                : 'Yolculuğa git'}{' '}
            ↗
          </Link>
        )}
      </footer>
    </section>
  );
}
